import * as React from 'react'
import { connect } from 'react-redux'
import { StackChildren, Text, Colors, Spacing } from '@doordash/design-language'
import { Interpolate } from '@doordash/lib-i18n'
import NavBar from '@doordash/component-navbar'
import styled from 'styled-components'
import { RouteComponentProps, withRouter } from 'react-router'

import { COUNTRY_SHORTCODES, DASHER_DIRECT_URL } from '../../../constants'
import Simple from '../../templates/simple'
import { SimpleStyleType } from '../../templates/simple/constants'
import { StripeCredentials } from '../../../models/types'
import { loadStripe } from '../../atoms/stripe'

import AddBankAccount from '../../molecules/add-bank-account'
import { i18nKeys } from 'translationTypes'
import i18n from '../../../i18n'
import Page from '../../molecules/page'
import PageRoot from '../../molecules/page-root'
import routes from '../../../routes'
import * as DA from '../../../typeModules/events'
import * as events from '../../../events'
import { BankAccountSetup as Tag } from '../../../models/selectorAnchors'

import * as actions from '../../../actions'

import t from './strings'

const NavBarContainer = styled.div`
  margin: -${Spacing.Medium}px;
`

export const Root = styled<{}, 'div'>('div')`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: ${Colors.PageBackgroundSecondary};
  padding-top: 70px;

  @media (max-width: 480px) {
    padding-top: 60px;
    background-color: ${Colors.SystemWhite};
  }
`

type BankAccountSetupProps = {
  country: keyof typeof COUNTRY_SHORTCODES
}

type ReduxProps = {
  errorMessage?: string
  stripeCredentials?: StripeCredentials
  getStripeCredentials?: (dasherId: string) => Promise<void>
  getPaymentAccount?: (dasherId: string) => Promise<void>
  dasherId?: string
  application?: object
  loadApplication?: (uniqueLink: string) => Promise<void>
  loadDasher?: () => Promise<void>
  applicantUniqueLink?: string
  clearErrors?: () => void
  markBankAccountSkip?: () => Promise<void>
  track?: <T>(...args: (DA.EventActions | T | object)[]) => Promise<void>
}

export const BankAccountSetup: React.FC<BankAccountSetupProps &
  ReduxProps &
  RouteComponentProps> = ({
  country,
  history,
  dasherId,
  getStripeCredentials,
  getPaymentAccount,
  stripeCredentials,
  loadApplication,
  loadDasher,
  applicantUniqueLink,
  markBankAccountSkip,
  track,
}) => {
  const [stripe, setStripe] = React.useState(null)

  React.useEffect(() => {
    track(DA.EventActions.Rendered, DA.AddBankAccountNames.AddBankAccount)
  }, [track])

  React.useEffect(() => {
    const loadPaymentData = async () => {
      if (applicantUniqueLink === null) {
        await loadDasher()
      }

      if (applicantUniqueLink !== null && dasherId === null) {
        await loadApplication(applicantUniqueLink)
      }

      if (dasherId && !stripeCredentials?.publicKey) {
        await getStripeCredentials(dasherId)
        await getPaymentAccount(dasherId)
      }

      if (stripeCredentials?.publicKey) {
        loadStripe(setStripe, stripeCredentials.publicKey)
      }
    }

    loadPaymentData()
  }, [
    applicantUniqueLink,
    dasherId,
    getStripeCredentials,
    stripeCredentials?.publicKey,
    getPaymentAccount,
    loadApplication,
    loadDasher,
  ])

  return (
    <StackChildren>
      {history?.action !== 'POP' && (
        <NavBarContainer>
          <NavBar
            onNavigationClick={() => {
              track(DA.EventActions.Clicked, DA.AddBankAccountNames.GoBack)
              history.goBack()
            }}
            textAlign={NavBar.TextAlignments.Center}
            isSeparatorVisible={false}
            data-anchor-id={Tag.BackButton}
          />
        </NavBarContainer>
      )}
      <Text styles={Text.Styles.PageTitle} data-anchor-id={Tag.Title}>
        {t.title()}
      </Text>
      <Text styles={Text.Styles.Body1} color={Colors.SystemGrey80}>
        <Interpolate
          i18n={i18n}
          i18nKey={i18nKeys.excitement__bank_account__subtitle}
          components={[
            <Text
              display={Text.DisplayModes.Inline}
              color={Colors.SystemGrey80}
              tag="a"
              target="_blank"
              href={DASHER_DIRECT_URL}
              onClick={() =>
                track(
                  DA.EventActions.Clicked,
                  DA.AddBankAccountNames.DasherDirectLearnMore
                )
              }
            />,
          ]}
        />
      </Text>
      <AddBankAccount
        country={country}
        stripe={stripe}
        onSuccess={() => history.push(routes.promotionsDownloadApp.path)}
        handleSecondaryAction={async () => {
          await markBankAccountSkip()
          return history.push(routes.promotionsDownloadApp.path)
        }}
        track={track}
      />
    </StackChildren>
  )
}

export const mapStateToProps = ({
  country,
  errorMessage,
  stripeCredentials,
  paymentAccount,
  dasherId,
  applicantUniqueLink,
}) => ({
  country,
  errorMessage,
  stripeCredentials,
  paymentAccount,
  dasherId,
  applicantUniqueLink,
})

export const mapDispatchToProps = {
  clearErrors: actions.clearErrors,
  getStripeCredentials: actions.getStripeCredentials,
  getPaymentAccount: actions.getPaymentAccount,
  loadDasher: actions.loadDasher,
  loadApplication: actions.loadApplication,
  markBankAccountSkip: actions.markBankAccountSkip,
  track: events.trackAddBankAccountPage,
}

const ConnectedBankAccountSetup = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(BankAccountSetup)
)

export const WrappedBankAccountSetup: React.FC<BankAccountSetupProps &
  ReduxProps> = ({ errorMessage, clearErrors, ...props }) => (
  <Simple
    includeFooter={false}
    headerIsDark={false}
    clearErrors={clearErrors}
    errorMessage={errorMessage}
    type={SimpleStyleType.Standard}
  >
    <Root>
      <Page>
        <PageRoot>
          <ConnectedBankAccountSetup {...props} />
        </PageRoot>
      </Page>
    </Root>
  </Simple>
)

export default ConnectedBankAccountSetup
